/**
 * Organization Settings Tab
 * Manages company/organization information
 */
import { useState, useEffect } from 'react';
import { useOrganizationSettings, useUpdateOrganizationSettings } from '@/hooks';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Building2, Save } from 'lucide-react';
import type { OrganizationSettings } from '@/lib/schemas';

export function OrganizationSettingsTab() {
  const { data, isLoading } = useOrganizationSettings();
  const updateSettings = useUpdateOrganizationSettings();

  const [formData, setFormData] = useState<Partial<OrganizationSettings>>({
    legal_name: '',
    dba_name: '',
    business_registration_number: '',
    tax_id_number: '',
    address: '',
    phone: '',
    email: '',
    remit_to_address: '',
  });

  // Initialize form with fetched data
  useEffect(() => {
    if (data) {
      setFormData(data);
    }
  }, [data]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateSettings.mutateAsync(formData);
  };

  const handleChange = (field: keyof OrganizationSettings, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value || null,
    }));
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b">
        <Building2 className="w-5 h-5 text-slate-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Organization Information</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage your company details and contact information
          </p>
        </div>
      </div>

      {/* Form Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Legal Name */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Legal Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="legal_name"
            value={formData.legal_name || ''}
            onChange={(e) => handleChange('legal_name', e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="ABC Construction Ltd."
          />
        </div>

        {/* DBA Name */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            DBA Name (Doing Business As)
          </label>
          <input
            type="text"
            name="dba_name"
            value={formData.dba_name || ''}
            onChange={(e) => handleChange('dba_name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="ABC Construction"
          />
          <p className="text-xs text-gray-500 mt-1">
            Optional - Use if your business operates under a different name
          </p>
        </div>

        {/* Business Registration Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Registration Number
          </label>
          <input
            type="text"
            name="business_registration_number"
            value={formData.business_registration_number || ''}
            onChange={(e) => handleChange('business_registration_number', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="BC1234567"
          />
        </div>

        {/* Tax ID Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tax ID / GST Number
          </label>
          <input
            type="text"
            name="tax_id_number"
            value={formData.tax_id_number || ''}
            onChange={(e) => handleChange('tax_id_number', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="123456789RT0001"
          />
        </div>

        {/* Address */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Address
          </label>
          <textarea
            name="address"
            value={formData.address || ''}
            onChange={(e) => handleChange('address', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="123 Business St&#10;Vancouver, BC V6B 1A1&#10;Canada"
          />
          <p className="text-xs text-gray-500 mt-1">
            Shown on invoices as your company's letterhead address
          </p>
        </div>

        {/* Remit-To Address (R5, SPEC-tenant-identity-settings-2026-08-17):
            OPTIONAL second address -- where PAYMENT should be sent, only when
            that differs from the Business Address above. Labelled and
            described so the distinction from Business Address is obvious
            without documentation (AC2). */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Remit-To Address <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            name="remit_to_address"
            value={formData.remit_to_address || ''}
            onChange={(e) => handleChange('remit_to_address', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="Leave blank to use the Business Address above"
          />
          <p className="text-xs text-gray-500 mt-1">
            Only fill this in if payments (e.g. cheques) should be mailed
            somewhere other than your Business Address above. Shown on
            invoices in its own "Remit Payment To" section, separate from
            your letterhead address. Most businesses should leave this blank.
          </p>
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone || ''}
            onChange={(e) => handleChange('phone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="(604) 555-1234"
          />
          <p className="text-xs text-gray-500 mt-1">
            Displayed on invoices and proposals
          </p>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Contact Email
          </label>
          <input
            type="email"
            name="email"
            value={formData.email || ''}
            onChange={(e) => handleChange('email', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="contact@abcconstruction.com"
          />
          <p className="text-xs text-gray-500 mt-1">
            Displayed on invoices and proposals
          </p>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t">
        <Button
          type="submit"
          disabled={updateSettings.isPending || !formData.legal_name}
          className="inline-flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {updateSettings.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
}
